import React, { useEffect } from "react";
import { Col, Form, Modal, Row } from "antd";
import { useState } from "react";
import OptionCard from "../OptionCard";
import { ResponseFields } from "@dreambigger/shared/src/types";
import { useSegment } from "@dreambigger/shared/src/hooks";

export interface RadioCardFormItemProps {
  className?: string;
  description?: string;
  fieldName: string;
  form: any;
  style?: React.CSSProperties;
  title?: string;
  titleColor?: string;
  optionsLabel?: string;
  options: Array<{
    label: string;
    value: string | boolean;
    description?: string;
    type?: "yes" | "no";
    activeColor?: string;
  }>;
  callout?: string;
  modalLaunchText?: string;
  modalTitle?: string | React.ReactNode;
  modalContent?: string | React.ReactNode;
  required?: boolean;
  onChange?: (value: ResponseFields) => void;
  segmentProperties?: Record<string, any>;
}

const DEFAULT_NO_COLOR = "#d4061e";
const DEFAULT_YES_COLOR = "#51c1b2";
const GRAY = "#d9d9d9";

const RadioCardFormItem = ({
  className,
  description,
  fieldName,
  form,
  style,
  title,
  titleColor,
  optionsLabel,
  options,
  callout,
  modalLaunchText = "Read More",
  modalTitle,
  modalContent,
  required,
  onChange,
  segmentProperties,
}: RadioCardFormItemProps) => {
  const [modalOpen, setModalOpen] = useState(false);
  const segment = useSegment();

  const displayModalContent = (content: string | React.ReactNode) => {
    if (!form || !fieldName || !options) {
      return null;
    }

    if (typeof content === "string") {
      return <p dangerouslySetInnerHTML={{ __html: content }} />;
    }
    return content;
  };

  const selectedValue = Form.useWatch(fieldName, form);

  // Trigger onChange event when selectedValue changes
  useEffect(() => {
    const values: ResponseFields = form.getFieldsValue();
    if (onChange) onChange(values);
  }, [selectedValue]);

  return (
    <>
      <Row
        className={`${className} ba bg-white pa-2`}
        style={style || { borderColor: GRAY }}
        gutter={8}
        align="stretch"
      >
        {/* TITLE AND CALLOUT - Ex: "GAP ---- ~$14/month" */}
        <Col xs={24} className="flex justify-between">
          {title && (
            <h3 className="f-5 sm_f-6 mb-2 lh-1" style={{ color: titleColor }}>
              {title}
            </h3>
          )}
          {callout && <p className="f-3 fwb sm_f-4 gray-8">{callout}</p>}
        </Col>

        {/* DESCRIPTION - "Ex: If your vehicle is deemed a total loss in an accident or stolen, GAP addresses the difference." */}
        {description && (
          <Col xs={24}>
            <div className="mb-4">
              <p
                className="mb-0"
                dangerouslySetInnerHTML={{ __html: description }}
              />
              {modalContent && (
                <span
                  className="fwb primary pointer"
                  onClick={() => {
                    setModalOpen(!modalOpen);
                    if (segmentProperties) {
                      segment.track({
                        action: "Modal Open",
                        label: `${fieldName} - ${modalLaunchText}`,
                        properties: segmentProperties,
                      });
                    }
                  }}
                >
                  {modalLaunchText}
                </span>
              )}
            </div>
            {optionsLabel && (
              <p className="mb-0 fwt">
                <span dangerouslySetInnerHTML={{ __html: optionsLabel }} />
                {required && <span className="warning ml-1">*</span>}
              </p>
            )}
          </Col>
        )}

        <Col xs={24}>
          <Row gutter={[16, 16]} align="stretch">
            {options.map((option, index) => {
              const { label, description, value, activeColor, type } = option;
              const activeColorStyle =
                activeColor || type === "no"
                  ? DEFAULT_NO_COLOR
                  : DEFAULT_YES_COLOR;

              return (
                <Col
                  xs={24}
                  sm={12}
                  key={label + index}
                  style={{ display: "flex" }}
                >
                  <Form.Item
                    noStyle
                    name={fieldName}
                    valuePropName="value"
                    className="h-100 w-100"
                  >
                    <OptionCard
                      className="mb-0 h-100 w-100"
                      label={label}
                      description={description}
                      activeColor={activeColorStyle}
                      selected={selectedValue === value}
                      onSelect={() => {
                        form.setFieldsValue({
                          [fieldName]: value,
                        });
                        if (segmentProperties) {
                          segment.track({
                            action: "Option Selected",
                            label: `${fieldName} - ${label}`,
                            properties: {
                              ...segmentProperties,
                              selectedValue: value,
                              selectedLabel: label,
                            },
                          });
                        }
                      }}
                      type={type}
                    />
                  </Form.Item>
                </Col>
              );
            })}
          </Row>
        </Col>
      </Row>
      <Modal
        title={displayModalContent(modalTitle)}
        open={modalOpen}
        footer={null}
        onCancel={() => setModalOpen(false)}
        style={{ top: 20 }}
      >
        {displayModalContent(modalContent)}
      </Modal>
    </>
  );
};

export default RadioCardFormItem;
